import floor from 'lodash/floor'
import type { OvertimeLimitForm } from '../SalarySettingsFormView'

interface OvertimeLimitNodeShape {
  threshold?: number | null
  salaryCode?: { id: string } | null
}

interface SalaryWorkTimeShape {
  overtimeLimits?: OvertimeLimitNodeShape[] | null
}

/**
 * Convert backend `overtimeLimits` node list into form state. Backend returns
 * threshold in seconds; form holds decimal hours.
 *
 * Cast guards backend type since `graphql-env.d.ts` is not yet regenerated.
 */
export const overtimeLimitsFromNode = (
  salaryWorkTime: unknown
): OvertimeLimitForm[] => {
  const nodes =
    (salaryWorkTime as SalaryWorkTimeShape | null | undefined)
      ?.overtimeLimits ?? []
  return nodes.map(node => ({
    threshold: floor((node.threshold ?? 0) / 3600, 2),
    salaryCode: node.salaryCode?.id ?? null,
  }))
}

/**
 * Build the `overtimeLimitsManual` mutation payload. Form holds decimal hours;
 * backend expects seconds. Returns `[]` (clears all tiers) when the current
 * mode does not support tiers (anything other than day-only with inline
 * overtime calculation against the static daily limit) so stale tier rows
 * never persist invisibly.
 */
export const overtimeLimitsToMutation = (
  useDayOvertime: boolean,
  limits: OvertimeLimitForm[],
  useWeekOvertime: boolean = false,
  calculateWeekOvertimeInline: boolean = false,
  usePlannerForOvertime: boolean = false
) =>
  useDayOvertime &&
  !useWeekOvertime &&
  calculateWeekOvertimeInline &&
  !usePlannerForOvertime
    ? limits.map(limit => ({
        threshold: limit.threshold * 3600,
        salaryCode: limit.salaryCode,
      }))
    : []
